/**
 * AuditLabPanel — the interactive centerpiece.
 *
 * Pick a matrix preset (or edit the entries of the four small operating
 * matrices) and run the four-condition audit live via algorithms/audit.ts.
 * Each condition reports pass/fail with the computed quantity (det, eigenvalue
 * gap, Tr(log M), exp/det residual). When all four pass, the verdict states
 * the system is structurally immune by construction; when the resource block
 * drifts toward the singular boundary the pre-singularity warning fires.
 *
 * The audit verifies *eligibility* for the structural guarantee — it is not a
 * runtime filter that catches attacks. Once A1–A4 hold, the ten NTs hold by
 * construction.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */
import React, { useMemo, useState } from "react";
import { PanelSurface, panelTokens } from "@csk/demo-ui";
import { cmatFromReal, toTuples } from "@csk/core/linear";
import {
  DEFAULT_PRESET_ID,
  MATRIX_PRESETS,
  presetById,
  type ConditionId,
  type Matrix,
} from "../data/matrixPresets.js";
import {
  SINGULARITY_THRESHOLD,
  auditAll,
  formatScalar,
} from "../algorithms/audit.js";
import type { ConditionResult } from "../algorithms/types.js";
interface BlockSpec {
  readonly key: keyof Editable;
  readonly label: string;
  readonly stratum: string;
  readonly token: string;
}
const BLOCKS: readonly BlockSpec[] = [
  {
    key: "authority",
    label: "Authority (A1 · G1)",
    stratum: "G1",
    token: "a1",
  },
  {
    key: "knowledge",
    label: "Knowledge (A2 · G2)",
    stratum: "G2",
    token: "a2",
  },
  {
    key: "temporal",
    label: "Temporal M (A3 · G3)",
    stratum: "G3",
    token: "a3",
  },
  {
    key: "resourceGenerator",
    label: "Resource generator X (A4 · G4)",
    stratum: "G4",
    token: "a4",
  },
];
const TOKEN_COLOR: Record<string, string> = {
  a1: panelTokens.a1,
  a2: panelTokens.a2,
  a3: panelTokens.a3,
  a4: panelTokens.a4,
};
interface Editable {
  authority: number[][];
  knowledge: number[][];
  temporal: number[][];
  resourceGenerator: number[][];
}
/**
 * Project a GL(n,ℂ) operating matrix onto the real entries the editor exposes
 * (the real part of each complex entry). The presets are real (im = 0), so this
 * is lossless for the shipped data; the editor edits these real entries and the
 * audit reads them back as genuine `ComplexMatrix` elements via `realToComplex`.
 */
function realPartsOf(m: Matrix): number[][] {
  return toTuples(m).map((row) => row.map((z) => z[0]));
}
/** Lift the editor's real n×n grid back into a GL(n,ℂ) element (im = 0). */
function realToComplex(m: number[][]): Matrix {
  return cmatFromReal(m);
}
function editableFrom(id: string): Editable {
  const p = presetById(id);
  return {
    authority: realPartsOf(p.authority),
    knowledge: realPartsOf(p.knowledge),
    temporal: realPartsOf(p.temporal),
    resourceGenerator: realPartsOf(p.resourceGenerator),
  };
}
const CONDITION_LABEL: Record<ConditionId, string> = {
  A1: "Authority subgroup closure",
  A2: "Spectral separation",
  A3: "Temporal trace stability",
  A4: "Resource master identity",
};
export function AuditLabPanel(): React.JSX.Element {
  const [presetId, setPresetId] = useState<string>(DEFAULT_PRESET_ID);
  const [matrices, setMatrices] = useState<Editable>(() =>
    editableFrom(DEFAULT_PRESET_ID),
  );
  const selectPreset = (id: string): void => {
    setPresetId(id);
    setMatrices(editableFrom(id));
  };
  const setEntry = (
    key: keyof Editable,
    i: number,
    j: number,
    raw: string,
  ): void => {
    const value = Number(raw);
    setMatrices((prev) => {
      const next = { ...prev, [key]: prev[key].map((row) => [...row]) };
      next[key][i][j] = Number.isFinite(value) ? value : 0;
      return next;
    });
  };
  const verdict = useMemo(
    () =>
      auditAll({
        authority: realToComplex(matrices.authority),
        knowledge: realToComplex(matrices.knowledge),
        temporal: realToComplex(matrices.temporal),
        resourceGenerator: realToComplex(matrices.resourceGenerator),
      }),
    [matrices],
  );
  const preset = presetById(presetId);
  const tone: "safe" | "unsafe" = verdict.immune ? "safe" : "unsafe";
  const verdictTitle = verdict.immune
    ? "All four conditions hold — structurally immune by construction"
    : `Condition${verdict.failing.length === 1 ? "" : "s"} ${verdict.failing.join(", ")} fail — not eligible for the structural guarantee`;
  const verdictBody = verdict.immune
    ? "All ten Neutralization Theorems hold unconditionally for this system. The ten vulnerability classes are structurally inexpressible — by construction, not by a runtime check."
    : `Until ${verdict.failing.join(" and ")} hold, the system is not embedded as an A1–A4 instance, so the corresponding vulnerability class is not yet structurally neutralized. The fail signature names the seam.`;
  return (
    <PanelSurface.PanelShell
      title="Matrix Audit Lab"
      subtitle="Run the four-condition audit live on a small matrix system. The audit verifies eligibility for the structural guarantee — not a runtime filter."
      accentColor={panelTokens.accentYellow}
    >
      <PanelSurface.Card>
        <PanelSurface.Prose className="m-0">
          Pick a preset, then edit any entry of the four operating matrices. The
          four matrices are genuine GL(n,ℂ) elements — the presets span GL(2,ℂ)
          and GL(3,ℂ) — audited through the{" "}
          <code className="text-[var(--mit-bright)]">@csk/core</code>{" "}
          realization (
          <code className="text-[var(--mit-bright)]">@csk/core/linear</code>):
          its dimension-general complex det / trace / matrix-exponential /
          eigenvalue primitives are exactly the operations the MIT certifier
          names. The editor edits real entries; the audit lifts them into ℂ.
          A2&apos;s spectral gap is the minimum pairwise eigenvalue distance
          computed in ℂ, so a rotation-block knowledge matrix reports its true
          conjugate-eigenpair separation rather than collapsing to zero. When
          all four conditions pass, immunity is structural — the ten
          Neutralization Theorems hold by construction.
        </PanelSurface.Prose>
      </PanelSurface.Card>

      <PanelSurface.SectionHeading>Choose a system</PanelSurface.SectionHeading>
      <PanelSurface.Card>
        <PanelSurface.ChipRow
          ariaLabel="Matrix preset"
          activeId={presetId}
          onChange={selectPreset}
          items={MATRIX_PRESETS.map((p) => ({
            id: p.id,
            label: p.title,
            color: panelTokens.accentYellow,
          }))}
        />
        <p className="mx-1 mt-3 mb-0 text-[12.5px] leading-[1.6] text-[var(--mit-muted)]">
          {preset.summary}
        </p>
      </PanelSurface.Card>

      <PanelSurface.SectionHeading>The four operating matrices</PanelSurface.SectionHeading>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {BLOCKS.map((block) => (
          <MatrixEditor
            key={block.key}
            spec={block}
            matrix={matrices[block.key]}
            onEntryChange={setEntry}
          />
        ))}
      </div>

      <PanelSurface.SectionHeading>Audit results</PanelSurface.SectionHeading>
      <div className="grid grid-cols-1 gap-3">
        {verdict.conditions.map((c) => (
          <ConditionCard key={c.id} result={c} />
        ))}
      </div>

      <PanelSurface.SectionHeading>Certification margin &amp; drift</PanelSurface.SectionHeading>
      <PanelSurface.Callout title="A continuous distance to decertification" tone="info">
        Each contingent condition carries a continuous margin, not just a
        pass/fail bit. Their combination is the distance to decertification — a
        scalar that is positive in the certified interior and zero on the
        boundary. Away from ties (where the binding condition is unique) it is
        differentiable, and its gradient is the four-component BOP drift vector:
        which block is under pressure, how fast, and which Neutralization
        Theorems lapse first.
      </PanelSurface.Callout>
      <PanelSurface.FeatureGrid
        ariaLabel="The four certification margins"
        items={[
          {
            id: "m1",
            eyebrow: "m₁ · authority (A1)",
            title: "Distance to the subgroup",
            color: panelTokens.a1,
            description:
              "How far the authority generators sit from the chosen subgroup H — a violation distance where 0 means certified. At risk: PERMIT, COMPOSE.",
            meta: <PanelSurface.Mono>{"m₁ = ‖Aᵢ·Aⱼ⁻¹ − proj_H‖_F"}</PanelSurface.Mono>,
          },
          {
            id: "m2",
            eyebrow: "m₂ · knowledge (A2)",
            title: "Spectral gap",
            color: panelTokens.a2,
            description:
              "The smallest distance between eigenvalues — the separation of distinct provenance channels. At risk: SEPARATE, DATA.ORIGIN, DATA.REACH.",
            meta: <PanelSurface.Mono>{"m₂ = min |λᵢ − λⱼ|"}</PanelSurface.Mono>,
          },
          {
            id: "m3",
            eyebrow: "m₃ · time (A3)",
            title: "Stability margin",
            color: panelTokens.a3,
            description:
              "How strongly temporal commitments bind: the largest eigenvalue real part must stay negative. At risk: PRECOMMIT, INV.ESTABLISH, INV.PRESERVE.",
            meta: <PanelSurface.Mono>{"m₃ = max Re(λ) < 0"}</PanelSurface.Mono>,
          },
          {
            id: "m4",
            eyebrow: "m₄ · resource (A4)",
            title: "Distance to singularity",
            color: panelTokens.a4,
            description:
              "The determinant magnitude of the resource block — the live signal the pre-singularity monitor below tracks. RES is automatic in GL(n).",
            meta: <PanelSurface.Mono>{"m₄ = |det X| > 0"}</PanelSurface.Mono>,
          },
        ]}
      />
      <PanelSurface.Card>
        <PanelSurface.Mono>
          {
            "d_cert = min( 1/(1+m₁) , m₂ , |m₃| , m₄ )   ·   ∇d_cert = the four-axis BOP drift vector"
          }
        </PanelSurface.Mono>
      </PanelSurface.Card>

      <PanelSurface.SectionHeading>
        Pre-singularity monitor (resource block)
      </PanelSurface.SectionHeading>
      <PanelSurface.Card>
        <PanelSurface.MeterBar
          label={`|det(exp X)| — resource block distance to the singular boundary`}
          value={Math.min(verdict.singularity.detMagnitude, 1)}
          min={0}
          max={1}
          tone={verdict.singularity.nearSingular ? "warn" : "safe"}
          color={
            verdict.singularity.nearSingular
              ? panelTokens.singularAmber
              : panelTokens.immuneGreen
          }
          valueLabel={formatScalar(verdict.singularity.detMagnitude)}
          description={
            verdict.singularity.nearSingular
              ? `Status: ${verdict.singularity.status}. |det| < ${String(SINGULARITY_THRESHOLD)} — the resource block is approaching M(n) \\ GL(n), where the resource obligation becomes unresolvable. The warning fires before the guarantee lapses (RD-56 §7).`
              : `Status: ${verdict.singularity.status}. The resource block is comfortably inside GL(n); det multiplicativity keeps RES.RELEASE invertible.`
          }
          ariaLabel="Resource block determinant magnitude"
        />
      </PanelSurface.Card>

      <PanelSurface.SectionHeading>Verdict</PanelSurface.SectionHeading>
      <PanelSurface.VerdictBanner tone={tone} title={verdictTitle} body={verdictBody} />

      <PanelSurface.SectionHeading>Audit trace</PanelSurface.SectionHeading>
      <PanelSurface.Card>
        <PanelSurface.Mono>
          {verdict.conditions
            .map((c) => `${c.id}  ${c.pass ? "PASS" : "FAIL"}  ${c.quantity}`)
            .join("\n")}
          {`
immune(system) = ${String(verdict.immune)}   -- all four conditions ${verdict.immune ? "hold" : "do not yet hold"}`}
        </PanelSurface.Mono>
      </PanelSurface.Card>

      <PanelSurface.RepositoryLinks
        items={[
          {
            label:
              "@csk/core/linear Mₙ(ℂ) realization (det / trace / exp / eigenvalues)",
            path: "realizations/typescript/src/linear/complex-matrix.ts",
          },
          {
            label: "MATRIX-VERIFICATION-SECURITY-GUIDE.md (audit checklist)",
            path: "theory/structural/MATRIX-VERIFICATION-SECURITY-GUIDE.md",
          },
          {
            label: "RD-56 §Security implications (pre-singularity)",
            path: "research/settled/RD-56-MATRIX-IMMUNITY-THEOREM.md",
          },
        ]}
      />
    </PanelSurface.PanelShell>
  );
}
interface MatrixEditorProps {
  readonly spec: BlockSpec;
  readonly matrix: number[][];
  readonly onEntryChange: (
    key: keyof Editable,
    i: number,
    j: number,
    raw: string,
  ) => void;
}
function MatrixEditor({
  spec,
  matrix,
  onEntryChange,
}: MatrixEditorProps): React.JSX.Element {
  const color = TOKEN_COLOR[spec.token] ?? panelTokens.accent;
  return (
    <div
      className="rounded-[12px] border border-[var(--mit-border)] bg-[rgba(5,7,16,0.5)] px-4 py-3.5"
      style={{ borderTop: `2px solid ${color}` }}
    >
      <div className="mb-2.5 text-[12.5px] font-semibold" style={{ color }}>
        {spec.label}
      </div>
      <div
        className="inline-grid gap-1.5"
        style={{
          gridTemplateColumns: `repeat(${String(matrix.length)}, minmax(0, 1fr))`,
        }}
      >
        {matrix.map((row, i) =>
          row.map((entry, j) => (
            <input
              key={`${String(i)}-${String(j)}`}
              type="number"
              step="0.1"
              value={Number.isFinite(entry) ? entry : 0}
              aria-label={`${spec.label} entry row ${String(i + 1)} column ${String(j + 1)}`}
              onChange={(event) => {
                onEntryChange(spec.key, i, j, event.target.value);
              }}
              className="w-[68px] rounded-[6px] border border-[var(--mit-border)] bg-[rgba(4,6,15,0.7)] px-2 py-1 text-center font-mono text-[12.5px] text-[var(--mit-bright)]"
            />
          )),
        )}
      </div>
    </div>
  );
}
interface ConditionCardProps {
  readonly result: ConditionResult;
}
function ConditionCard({ result }: ConditionCardProps): React.JSX.Element {
  const color = result.pass ? panelTokens.immuneGreen : panelTokens.singularRed;
  return (
    <div
      className="rounded-[12px] border border-[var(--mit-border)] bg-[rgba(5,7,16,0.5)] px-4 py-3.5"
      style={{ borderLeft: `3px solid ${color}` }}
    >
      <div className="mb-1.5 flex flex-wrap items-center gap-2">
        <span className="font-mono text-[13px] font-bold" style={{ color }}>
          {result.id}
        </span>
        <span className="text-[13px] font-semibold text-[var(--mit-text)]">
          {CONDITION_LABEL[result.id]}
        </span>
        {PanelSurface.badge(result.pass ? "PASS" : "FAIL", color)}
        <span className="ml-auto font-mono text-[12px] text-[var(--mit-a3)]">
          {result.quantity}
        </span>
      </div>
      <div className="text-[12.5px] leading-[1.6] text-[var(--mit-text)]">
        {result.detail}
      </div>
    </div>
  );
}
